import { getFunctions, httpsCallable } from "firebase/functions";
import { callDebugLog } from "./callDebug";

/**
 * Pre-grant camera/mic so Zego createZegoStream is faster. Stops tracks immediately.
 */
function logMediaPermissionProbe() {
  callDebugLog("[media-permission]", {
    hasMediaDevices: !!navigator.mediaDevices,
    hasGetUserMedia: !!navigator.mediaDevices?.getUserMedia,
    locationProtocol: window.location.protocol,
    userAgent: navigator.userAgent,
  });

  navigator.permissions
    ?.query({ name: "camera" })
    .then((r) => callDebugLog("[camera permission]", r.state))
    .catch(console.warn);

  navigator.permissions
    ?.query({ name: "microphone" })
    .then((r) => callDebugLog("[mic permission]", r.state))
    .catch(console.warn);
}

export async function warmupCallMedia() {
  const warmupStart = performance.now();

  callDebugLog("[callPerf] warmupCallMedia start", { elapsed: 0 });
  callDebugLog("[mediaWarmupPerf] start", { ms: 0 });

  try {
    if (typeof navigator !== "undefined") {
      logMediaPermissionProbe();
    }

    if (
      typeof navigator === "undefined" ||
      !navigator.mediaDevices?.getUserMedia
    ) {
      callDebugLog("[callPerf] warmupCallMedia unsupported", {
        elapsed: Math.round(performance.now() - warmupStart),
      });
      callDebugLog("[callMediaWarmup] failed", { reason: "unsupported" });
      callDebugLog("[mediaWarmupPerf] failed ms", {
        ms: Date.now() - warmupStart,
        reason: "unsupported",
      });
      return false;
    }

    const getUserMediaStart = performance.now();
    callDebugLog("[callPerf] warmupCallMedia getUserMedia start", {
      elapsed: Math.round(performance.now() - warmupStart),
    });

    const stream = await navigator.mediaDevices.getUserMedia({
      audio: true,
      video: true,
    });

    callDebugLog("[callPerf] warmupCallMedia getUserMedia success", {
      elapsed: Math.round(performance.now() - warmupStart),
      getUserMediaMs: Math.round(performance.now() - getUserMediaStart),
    });

    stream.getTracks().forEach((track) => {
      try {
        track.stop();
      } catch (_) {}
    });

    callDebugLog("[callPerf] warmupCallMedia tracks stopped", {
      elapsed: Math.round(performance.now() - warmupStart),
    });
    callDebugLog("[callPerf] warmupCallMedia end", {
      elapsed: Math.round(performance.now() - warmupStart),
      ok: true,
    });
    callDebugLog("[callMediaWarmup] success");
    callDebugLog("[mediaWarmupPerf] success ms", {
      ms: Math.round(performance.now() - warmupStart),
    });
    return true;
  } catch (err) {
    callDebugLog("[callPerf] warmupCallMedia getUserMedia fail", {
      elapsed: Math.round(performance.now() - warmupStart),
      error: err?.message || err,
    });
    callDebugLog("[callPerf] warmupCallMedia end", {
      elapsed: Math.round(performance.now() - warmupStart),
      ok: false,
    });
    callDebugLog("[callMediaWarmup] failed", {
      error: err?.message || err,
    });
    callDebugLog("[mediaWarmupPerf] failed ms", {
      ms: Math.round(performance.now() - warmupStart),
      error: err?.message || err,
    });
    return false;
  }
}

/**
 * Background Zego token for CallRoom bootstrap (same CF as in-room fetch).
 */
export async function prefetchZegoBootstrap(callId, userName) {
  if (!callId) return null;

  const prefetchStart = performance.now();
  callDebugLog("[callPerf] prefetchZegoBootstrap start", {
    callId,
    elapsed: 0,
  });

  try {
    const functions = getFunctions(undefined, "us-central1");
    const generateZegoKitToken = httpsCallable(functions, "generateZegoKitToken");

    const res = await generateZegoKitToken({
      roomId: callId,
      userName: userName || "User",
    });

    const data = res?.data || {};
    const token = String(data.token || "");

    if (!data.ok || !token) {
      callDebugLog("[callPerf] prefetchZegoBootstrap end", {
        callId,
        elapsed: Math.round(performance.now() - prefetchStart),
        ok: false,
        reason: "invalid_token",
      });
      console.warn("[callRoom] prefetch token invalid", { callId });
      return null;
    }

    callDebugLog("[callPerf] prefetchZegoBootstrap end", {
      callId,
      elapsed: Math.round(performance.now() - prefetchStart),
      ok: true,
    });

    return {
      ...data,
      roomId: String(data.roomId || callId),
    };
  } catch (err) {
    callDebugLog("[callPerf] prefetchZegoBootstrap end", {
      callId,
      elapsed: Math.round(performance.now() - prefetchStart),
      ok: false,
      error: err?.message || err,
    });
    console.warn("[callRoom] prefetch token failed", {
      callId,
      error: err?.message || err,
    });
    return null;
  }
}

export function prefetchZegoBootstrapWithTimeout(callId, userName, timeoutMs = 2500) {
  const raceStart = performance.now();
  callDebugLog("[callPerf] prefetchZegoBootstrapWithTimeout start", {
    callId,
    timeoutMs,
    elapsed: 0,
  });

  return Promise.race([
    prefetchZegoBootstrap(callId, userName),
    new Promise((resolve) => {
      setTimeout(() => resolve(null), timeoutMs);
    }),
  ]).then((result) => {
    callDebugLog("[callPerf] prefetchZegoBootstrapWithTimeout end", {
      callId,
      elapsed: Math.round(performance.now() - raceStart),
      hasBootstrap: !!result?.token,
      timedOut: !result,
    });
    return result;
  });
}
